import { useEffect, useState } from 'react';
import Input from './Input';
import Button from './Button';
import Modal from './Modal';
import useCopy from '../util/useCopy';
import { LetterboxdUserSearchResults } from '../resources/Letterboxd';
import { makeClassName } from '@siteless/util';
import { updateUserPropertiesMutation } from '../resources/Auth/mutations';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import Action from './Action';
import { LetterboxdAccountDisplay } from './LetterboxdAccountDisplay';
import useDebouncedValue from 'hooks/src/useDebouncedValue';

const LetterboxdModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const copy = useCopy();
  const currentUser = CurrentUserMustResource.use();

  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const debouncedSearchText = useDebouncedValue(searchText, 500);

  const [searchResults] =
    LetterboxdUserSearchResults.useLoadable(debouncedSearchText);

  const [selectedSearchResult, setSelectedSearchResult] = useState<string>();

  useEffect(() => {
    setSelectedSearchResult(undefined);
  }, [searchText]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="LetterboxdModal">
      {currentUser.letterboxdId && !isEditing ? (
        <>
          <Modal.MainText>
            <div className="mb-4 rounded-xl bg-neutral-100 p-4 py-4">
              <p>{copy['Your Letterboxd account is connected.']}</p>
              <LetterboxdAccountDisplay
                letterboxdId={currentUser.letterboxdId}
              />
            </div>
            <p className="text-sm text-neutral-400">
              {copy.letterboxdDisclaimer}
            </p>
          </Modal.MainText>
          <Modal.BottomActions className="flex-none">
            <Action
              stacked
              onClick={() => {
                if (!window.confirm('Are you sure you want to disconnect?'))
                  return;
                updateUserPropertiesMutation({
                  userId: currentUser.id,
                  letterboxdId: null,
                });
              }}
            >
              {copy['disconnect this account']}
            </Action>
            <Action
              stacked
              onClick={() => {
                setIsEditing(true);
              }}
            >
              {copy['connect a different account']}
            </Action>
            <Button onClick={onClose} stacked>
              Done
            </Button>
          </Modal.BottomActions>
        </>
      ) : (
        <>
          {searchResults === null ? (
            <Modal.MainText>
              <p>
                {
                  copy[
                    'Connect Letterboxd to sync your watched films with Fantasy Oscars.'
                  ]
                }
              </p>
              <p className="text-sm text-neutral-400">
                {copy.letterboxdDisclaimer}
              </p>
            </Modal.MainText>
          ) : searchResults === undefined ? (
            <div className="flex min-h-0 flex-1 flex-col-reverse overflow-auto px-5">
              {new Array(5).fill(0).map((_, i) => (
                <div
                  key={i}
                  className="mb-4 flex items-center gap-4 self-stretch rounded-xl border-4 border-neutral-100 bg-neutral-100 p-3"
                >
                  <div className="h-16 w-16 flex-none animate-pulse rounded-full bg-neutral-200" />
                  <div className="flex-1 space-y-3">
                    <div className="h-4 animate-pulse rounded bg-neutral-200" />
                    <div className="h-4 animate-pulse rounded bg-neutral-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : searchResults.status === 'error' ? (
            <Modal.MainText>
              <p>{copy['Letterboxd is unavailable right now']}</p>
            </Modal.MainText>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col-reverse overflow-auto px-5">
              {searchResults.data.length === 0 ? (
                <p>
                  {
                    copy[
                      "Hmmm, we weren't able to find that letterboxd username."
                    ]
                  }
                </p>
              ) : (
                searchResults.data.map((result) => (
                  <div
                    key={result.id}
                    className={makeClassName(
                      'mb-4 flex items-center gap-4 rounded-xl border-4 p-3',
                      selectedSearchResult === result.id
                        ? 'border-rose-bright text-rose-default bg-transparent'
                        : 'border-neutral-100 bg-neutral-100',
                    )}
                    onClick={() => {
                      setSelectedSearchResult(result.id);
                    }}
                  >
                    <img
                      src={result.avatarUrl}
                      alt=""
                      className="h-16 w-16 flex-none rounded-full"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{result.displayName}</div>
                      <div className="text-neutral-500">{result.username}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <Modal.BottomActions className="flex-none">
            {!debouncedSearchText && <p>{copy.letterboxdInstructions}</p>}
            <Input stacked value={searchText} onChange={setSearchText} />
            <Button
              isLoading={isLoading}
              stacked
              disabled={!selectedSearchResult}
              onClick={async () => {
                if (selectedSearchResult) {
                  setIsLoading(true);
                  await updateUserPropertiesMutation({
                    userId: currentUser.id,
                    letterboxdId: selectedSearchResult,
                  });
                  setIsLoading(false);
                }
              }}
            >
              {copy["That's me!"]}
            </Button>
            {isEditing && (
              <Action
                stacked
                onClick={() => {
                  setIsEditing(false);
                }}
              >
                Cancel
              </Action>
            )}
          </Modal.BottomActions>
        </>
      )}
    </Modal>
  );
};

export default LetterboxdModal;
